/**
 * PatientDeduplicationPage Component
 *
 * Page for identifying and merging duplicate patient records.
 * Supports two flows:
 *   1. Manual — Find Duplicates → select patients → merge customization page
 *   2. Auto-Merge — scan for conflict-free groups → review → batch merge
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetPatientVisibilityQuery } from '@features/organizations';
import { useCan, useOrganizationId } from '@features/user';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import {
  AutoMergeProgressOverlay,
  AutoMergeResultsAlert,
  AutoMergeReviewPanel,
} from './components/auto-merge';
import { PatientDeduplicationErrorBoundary } from './components/PatientDeduplicationErrorBoundary';
import { useAutoMerge } from './hooks/useAutoMerge';
import type { MergeCustomizationLocationState } from './types/merge-customization';
import { useDismissDuplicatePairMutation, useFindDuplicatesMutation } from './store';
import type { PatientGroup } from './types';

const PatientDeduplicationPageInner: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const organizationId = useOrganizationId();
  const isAdmin = useCan('manage_organization');
  const { data: visibilityData } = useGetPatientVisibilityQuery();
  const isOrgWide = visibilityData?.mode === 'organization_wide';
  const { patientDeduplication } = useEventTracking();

  // Set when the user entered dedup from the patient-import review wizard.
  // Drives "return to import" affordances and the post-merge redirect.
  const importSessionId =
    (location.state as { importSessionId?: string } | null)?.importSessionId;

  const returnToImport = useCallback(() => {
    if (importSessionId) navigate(`/patients/import/${importSessionId}`);
  }, [importSessionId, navigate]);

  // Auto-merge hook
  const autoMerge = useAutoMerge(organizationId);
  const isAutoMergeActive = autoMerge.state.status !== 'idle';

  // Manual dedup state
  const [deduplicationResult, setDeduplicationResult] = useState<{
    duplicate_groups: PatientGroup[];
    total_patients: number;
    total_duplicates_found: number;
  } | null>(null);

  const [selectedPatients, setSelectedPatients] = useState<Record<string, Set<string>>>({});

  // Track groups currently being dismissed (for loading state)
  const [dismissingGroups, setDismissingGroups] = useState<Set<string>>(new Set());

  const [findDuplicates, { isLoading: isDeduplicating }] = useFindDuplicatesMutation();
  const [dismissDuplicatePair] = useDismissDuplicatePairMutation();

  // Handlers
  const handleFindDuplicates = useCallback(async () => {
    if (!organizationId) {
      enqueueSnackbar('Organization not found', { variant: 'error' });
      return;
    }

    patientDeduplication.findDuplicatesClicked({ organization_id: organizationId });

    const startTime = performance.now();

    try {
      const result = await findDuplicates({
        findDuplicatesRequest: {
          organization_id: organizationId,
          confidence_threshold: 60,
        },
      }).unwrap();

      const scanDurationMs = Math.round(performance.now() - startTime);

      patientDeduplication.scanCompleted({
        organization_id: organizationId,
        total_patients: result.total_patients,
        duplicate_groups_found: result.duplicate_groups.length,
        total_duplicates_found: result.total_duplicates_found,
        scan_duration_ms: scanDurationMs,
      });

      setDeduplicationResult(result);

      const initial: Record<string, Set<string>> = {};
      for (const group of result.duplicate_groups) {
        initial[group.group_id] = new Set(group.patients.map(p => p.patient_id));
      }
      setSelectedPatients(initial);

      enqueueSnackbar(
        `Found ${result.total_duplicates_found} potential duplicates in ${result.duplicate_groups.length} groups`,
        { variant: 'success' },
      );
    } catch (error) {
      patientDeduplication.scanFailed(
        error instanceof Error ? error.message : 'Unknown error',
        { organization_id: organizationId },
      );

      enqueueSnackbar('Failed to find duplicates', { variant: 'error' });
      console.error('Deduplication error:', error);
    }
  }, [organizationId, findDuplicates, enqueueSnackbar, patientDeduplication]);

  // Auto-trigger find duplicates on first mount
  const autoTriggeredRef = useRef(false);
  useEffect(() => {
    if (autoTriggeredRef.current || !organizationId || !isAdmin || !isOrgWide) return;
    autoTriggeredRef.current = true;
    void handleFindDuplicates();
  }, [organizationId, isAdmin, isOrgWide, handleFindDuplicates]);

  // When the user entered from the import flow and all duplicate groups have
  // been resolved (merged or dismissed), bounce back to the import session
  // review so they can finish the import.
  useEffect(() => {
    if (!importSessionId) return;
    if (!deduplicationResult) return;
    if (deduplicationResult.duplicate_groups.length > 0) return;
    returnToImport();
  }, [importSessionId, deduplicationResult, returnToImport]);

  const handleTogglePatient = useCallback((groupId: string, patientId: string) => {
    setSelectedPatients(prev => {
      const current = prev[groupId] ?? new Set<string>();
      const next = new Set(current);
      if (next.has(patientId)) {
        next.delete(patientId);
      } else {
        next.add(patientId);
      }
      return { ...prev, [groupId]: next };
    });
  }, []);

  const handleMergeGroup = useCallback(
    (group: PatientGroup) => {
      if (!organizationId) {
        enqueueSnackbar('Organization not found', { variant: 'error' });
        return;
      }

      const checked = selectedPatients[group.group_id];
      if (!checked || checked.size < 2) {
        enqueueSnackbar('Select at least 2 patients to merge', { variant: 'warning' });
        return;
      }

      const filteredGroup: PatientGroup = {
        ...group,
        patients: group.patients.filter(p => checked.has(p.patient_id)),
      };

      patientDeduplication.mergeInitiated({
        organization_id: organizationId,
        group_id: group.group_id,
        selected_patient_count: checked.size,
      });

      const navState: MergeCustomizationLocationState = {
        group: filteredGroup,
        organizationId,
        importSessionId,
      };
      navigate('/patients/merge/customize', { state: navState });
    },
    [organizationId, importSessionId, selectedPatients, navigate, enqueueSnackbar, patientDeduplication],
  );

  const handleDismissGroup = useCallback(
    async (group: PatientGroup) => {
      if (!organizationId) {
        enqueueSnackbar('Organization not found', { variant: 'error' });
        return;
      }

      const checked = selectedPatients[group.group_id];
      const patientIds = group.patients
        .filter(p => checked?.has(p.patient_id))
        .map(p => p.patient_id);
      if (patientIds.length < 2) return;

      patientDeduplication.dismissClicked({
        organization_id: organizationId,
        group_id: group.group_id,
        patient_count: patientIds.length,
      });

      setDismissingGroups(prev => new Set(prev).add(group.group_id));

      try {
        await dismissDuplicatePair({
          dismissDuplicateRequest: {
            organization_id: organizationId,
            patient_ids: patientIds,
          },
        }).unwrap();

        patientDeduplication.dismissCompleted({
          organization_id: organizationId,
          group_id: group.group_id,
          patient_count: patientIds.length,
        });

        // Remove group from results
        setDeduplicationResult(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            duplicate_groups: prev.duplicate_groups.filter(g => g.group_id !== group.group_id),
            total_duplicates_found: prev.total_duplicates_found - patientIds.length,
          };
        });

        // Clean up selection state
        setSelectedPatients(prev => {
          const next = { ...prev };
          delete next[group.group_id];
          return next;
        });

        enqueueSnackbar('Patients marked as not duplicates', { variant: 'success' });
      } catch (error) {
        patientDeduplication.dismissFailed(
          error instanceof Error ? error.message : 'Unknown error',
          { organization_id: organizationId, group_id: group.group_id },
        );
        enqueueSnackbar('Failed to dismiss duplicate group', { variant: 'error' });
      } finally {
        setDismissingGroups(prev => {
          const next = new Set(prev);
          next.delete(group.group_id);
          return next;
        });
      }
    },
    [organizationId, selectedPatients, dismissDuplicatePair, enqueueSnackbar, patientDeduplication],
  );

  // Access control
  if (!isAdmin) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        <Alert severity="error">
          <AlertTitle>Access Denied</AlertTitle>
          Only practice administrators can access patient deduplication.
        </Alert>
      </Box>
    );
  }

  if (!isOrgWide) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        <Alert severity="warning">
          <AlertTitle>Organization-Wide Sharing Required</AlertTitle>
          Enable organization-wide patient sharing in Practice Settings to use patient deduplication.
        </Alert>
      </Box>
    );
  }

  if (!organizationId) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        <Alert severity="warning">
          <AlertTitle>Organization Not Found</AlertTitle>
          Unable to load organization information. Please try logging out and back in.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            Patient Deduplication
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.25 }}>
            Identify and merge duplicate patient records
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleFindDuplicates}
            disabled={isDeduplicating || isAutoMergeActive}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              borderRadius: 2,
            }}
          >
            {isDeduplicating ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {isDeduplicating ? 'Finding Duplicates...' : 'Find Duplicates'}
          </Button>
          <Button
            variant="contained"
            onClick={autoMerge.startAutoMerge}
            disabled={isDeduplicating || isAutoMergeActive}
            startIcon={<AutoFixHighIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              borderRadius: 2,
            }}
          >
            Auto-Merge
          </Button>
        </Box>
      </Box>

      {/* Import-flow banner — surfaces an escape hatch back to the active session */}
      {importSessionId && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={returnToImport}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Return to Import
            </Button>
          }
        >
          You&apos;re resolving duplicates as part of an active patient import.
          You&apos;ll be returned to the import review once these are cleared.
        </Alert>
      )}

      {/* Auto-merge states */}
      {autoMerge.state.status === 'scanning' && (
        <AutoMergeProgressOverlay message="Scanning for auto-merge candidates..." />
      )}

      {autoMerge.state.status === 'reviewing' && (
        <AutoMergeReviewPanel
          candidates={autoMerge.state.candidates}
          deselectedGroups={autoMerge.state.deselectedGroups}
          approvedCount={autoMerge.approvedCount}
          skippedGroups={autoMerge.state.skippedGroups}
          onToggleGroup={autoMerge.toggleGroupSelection}
          onSelectAll={autoMerge.selectAllGroups}
          onDeselectAll={autoMerge.deselectAllGroups}
          onConfirm={autoMerge.confirmBatchMerge}
          onCancel={autoMerge.cancelAutoMerge}
        />
      )}

      {autoMerge.state.status === 'merging' && (
        <AutoMergeProgressOverlay
          message={`Merging ${autoMerge.approvedCount} group${autoMerge.approvedCount !== 1 ? 's' : ''}...`}
          processed={autoMerge.jobProgress?.processed}
          total={autoMerge.jobProgress?.total}
        />
      )}

      {autoMerge.state.status === 'complete' && autoMerge.state.mergeResult && (
        <AutoMergeResultsAlert
          result={autoMerge.state.mergeResult}
          onDone={() => {
            autoMerge.cancelAutoMerge();
            if (importSessionId) returnToImport();
          }}
        />
      )}

      {autoMerge.state.status === 'error' && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={autoMerge.startAutoMerge} sx={{ textTransform: 'none' }}>
              Retry
            </Button>
          }
          sx={{ mb: 3 }}
        >
          <AlertTitle>Auto-Merge Error</AlertTitle>
          {autoMerge.state.error ?? 'An unexpected error occurred'}
        </Alert>
      )}

      {/* Manual dedup UI — only show when auto-merge is idle */}
      {!isAutoMergeActive && (
        <>
          {/* Results Summary */}
          {deduplicationResult && (
            <Alert severity="info" sx={{ mb: 3 }} data-testid="dedupe-results-summary">
              Found {deduplicationResult.total_duplicates_found} potential duplicates in{' '}
              {deduplicationResult.duplicate_groups.length} groups out of{' '}
              {deduplicationResult.total_patients} total patients
            </Alert>
          )}

          {/* Empty State */}
          {!deduplicationResult && !isDeduplicating && (
            <Paper
              sx={{
                p: 6,
                textAlign: 'center',
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                No Duplicate Search Results
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Click &ldquo;Find Duplicates&rdquo; to scan for potential duplicate patient records,
                or use &ldquo;Auto-Merge&rdquo; to automatically merge conflict-free groups
              </Typography>
            </Paper>
          )}

          {/* No Duplicates Found */}
          {deduplicationResult && deduplicationResult.duplicate_groups.length === 0 && (
            <Paper
              sx={{
                p: 6,
                textAlign: 'center',
                backgroundColor: alpha(theme.palette.success.main, 0.05),
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, color: theme.palette.success.main }}>
                No Duplicates Found
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                All patient records appear to be unique
              </Typography>
            </Paper>
          )}

          {/* Duplicate Groups */}
          {deduplicationResult &&
            deduplicationResult.duplicate_groups.map((group, groupIndex) => {
              const checked = selectedPatients[group.group_id] ?? new Set<string>();
              const checkedCount = checked.size;

              return (
                <Paper
                  key={group.group_id}
                  sx={{
                    mb: 3,
                    overflow: 'hidden',
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {/* Group Header */}
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Group {groupIndex + 1}
                      </Typography>
                      {group.match_reasons.map(reason => (
                        <Chip
                          key={reason}
                          label={reason
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, c => c.toUpperCase())}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ fontWeight: 500 }}
                        />
                      ))}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        color="inherit"
                        onClick={() => handleDismissGroup(group)}
                        disabled={dismissingGroups.has(group.group_id)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 2,
                          borderRadius: 1.5,
                          color: theme.palette.text.primary,
                          borderColor: theme.palette.grey[400],
                        }}
                      >
                        {dismissingGroups.has(group.group_id) ? (
                          <CircularProgress size={18} sx={{ mr: 1 }} />
                        ) : null}
                        {`Not Duplicates (${checkedCount})`}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleMergeGroup(group)}
                        disabled={checkedCount < 2 || dismissingGroups.has(group.group_id)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 2,
                          borderRadius: 1.5,
                        }}
                      >
                        {`Merge Selected (${checkedCount})`}
                      </Button>
                    </Box>
                  </Box>

                  {/* Patient Table */}
                  <TableContainer>
                    <Table aria-label={`Duplicate group ${groupIndex + 1}`}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{padding: "checkbox",  fontWeight: 600, width: 56 }}>
                            Include
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>First Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Last Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>DOB</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Insurance</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Policy #</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Payer ID</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Last Appt</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.patients.map((patient, patientIndex) => {
                          const isChecked = checked.has(patient.patient_id);
                          const rowBackgroundColor =
                            patientIndex % 2 === 0
                              ? 'transparent'
                              : alpha(theme.palette.grey[500], 0.03);

                          return (
                            <TableRow
                              key={patient.patient_id}
                              sx={{
                                backgroundColor: rowBackgroundColor,
                                opacity: isChecked ? 1 : 0.5,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                },
                              }}
                            >
                              <TableCell sx={{ padding: "checkbox" }}>
                                <Checkbox
                                  checked={isChecked}
                                  onChange={() =>
                                    handleTogglePatient(group.group_id, patient.patient_id)
                                  }
                                  color="primary"
                                 slotProps={{ input: {
                                    'aria-label': `Include ${patient.first_name ?? ''} ${patient.last_name ?? ''} in merge`,
                                  } }}/>
                              </TableCell>
                              <TableCell>{patient.first_name || '—'}</TableCell>
                              <TableCell>{patient.last_name || '—'}</TableCell>
                              <TableCell>
                                {patient.date_of_birth
                                  ? new Date(patient.date_of_birth).toLocaleDateString()
                                  : '—'}
                              </TableCell>
                              <TableCell>{patient.email || '—'}</TableCell>
                              <TableCell>{patient.mobile_phone || '—'}</TableCell>
                              <TableCell>{patient.insurance_provider || '—'}</TableCell>
                              <TableCell>{patient.insurance_policy_number || '—'}</TableCell>
                              <TableCell>{patient.insurance_payer_id || '—'}</TableCell>
                              <TableCell>
                                {new Date(patient.created_at).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                {patient.last_appointment
                                  ? new Date(patient.last_appointment).toLocaleDateString()
                                  : '—'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              );
            })}
        </>
      )}
    </Box>
  );
};

export const PatientDeduplicationPage: React.FC = () => (
  <PatientDeduplicationErrorBoundary>
    <PatientDeduplicationPageInner />
  </PatientDeduplicationErrorBoundary>
);

export default PatientDeduplicationPage;
